import { useEffect, useMemo, useState } from "react";
import ButtonGroup from "../components/layout/ButtonGroup";
import Button from "../components/ui/Button";
import Navbar from "../components/sections/Navbar";
import PageHeader from "../components/sections/PageHeader";
import Breadcrumb from "../components/ui/Breadcrumb";
import Divider from "../components/ui/Divider";
import ProfileSection from "../components/sections/ProfileSection";
import { useAuth } from "../context/AuthContext";
import "./Profile.css";

const initialProfile = {
  first_name: "",
  email: "",
  role: "user",
  region: null,
  gemeente: null,
  function_title: "",
  function_description: "",
  password: "",
};

function Profile() {
  const { user: authUser, tokens, setUser, authorizedFetch } = useAuth();
  const [profile, setProfile] = useState(initialProfile);
  const [status, setStatus] = useState(null);
  const [locationOptions, setLocationOptions] = useState({ regions: [], gemeentes: [] });
  const canEdit = useMemo(
    () => authUser?.role === "admin" || authUser?.role === "superadmin",
    [authUser?.role]
  );

  useEffect(() => {
    if (!authUser || !tokens?.access) return;
    authorizedFetch("/api/account_locations/")
      .then((res) => res.json())
      .then((data) => setLocationOptions(data || { regions: [], gemeentes: [] }))
      .catch(() => setLocationOptions({ regions: [], gemeentes: [] }));
  }, [authUser, tokens?.access, authorizedFetch]);

  useEffect(() => {
    if (!authUser) return;
    setProfile((prev) => ({
      ...prev,
      first_name: authUser.first_name || "",
      email: authUser.email || "",
      role: authUser.role || "user",
      region: authUser.region || null,
      gemeente: authUser.gemeente || null,
      function_title: authUser.function_title || "",
      function_description: authUser.function_description || "",
      password: "",
    }));
  }, [authUser]);

  const handleChange = (field, value) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReset = () => {
    if (!authUser) return;
    setProfile({
      first_name: authUser.first_name || "",
      email: authUser.email || "",
      role: authUser.role || "user",
      region: authUser.region || null,
      gemeente: authUser.gemeente || null,
      function_title: authUser.function_title || "",
      function_description: authUser.function_description || "",
      password: "",
    });
    setStatus(null);
  };

  const handleSave = async () => {
    if (!canEdit || !tokens?.access) return;

    setStatus("Bezig met opslaan...");
    try {
      const response = await authorizedFetch("/api/users/me/", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profile),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload?.detail || "Opslaan mislukt");
      }

      const updated = await response.json();
      setProfile((prev) => ({
        ...prev,
        ...updated,
        password: "",
      }));
      setUser(updated);
      setStatus("Opgeslagen");
    } catch (err) {
      setStatus(err.message || "Opslaan mislukt");
    }
  };

    return (
        <div>
            <Navbar />
            <div className="container">
                <Breadcrumb icon="home-line.png" items={["Profiel"]} current="Mijn profiel" />
                <PageHeader title="Mijn profiel" showSearch={false} />
                <Divider mt={16} mb={24} />
                <div className="profile">
                    <div
                        className="gradient-header"
                        style={{
                            backgroundImage: `url("/gradient-profile.png")`,
                            height: "240px",
                        }}
                    />
                    <div className="profile-header">
                        <div className="group">
                            <img src="/avatar-profile.png" alt="Profielfoto" />
                            <div className="profile-text">
                                <h2>Profiel</h2>
                                <span>Bekijk of update hier jouw gegevens.</span>
                            </div>
                        </div>
                        {canEdit && (
                            <div className="page-actions">
                                <ButtonGroup>
                                    <Button variant="secondary" onClick={handleReset}>Annuleren</Button>
                                    <Button variant="primary" onClick={handleSave}>Opslaan</Button>
                                </ButtonGroup>
                            </div>
                        )}
                    </div>
                </div>
                {status && <p className="form-hint" style={{ marginTop: 8 }}>{status}</p>}
                <ProfileSection
                    profile={profile}
                    onChange={handleChange}
                    readOnly={!canEdit}
                    locationOptions={locationOptions}
                    showPassword={false}
                />
            </div>
        </div>
    );
}

export default Profile;
